import { Directive, DoCheck, Input, OnDestroy, inject } from '@angular/core';
import { GridComponent } from '@progress/kendo-angular-grid';
import {
  RYN_REPLAY_GRID_SNAPSHOTS_CONFIG,
  RynReplayGridSnapshotService,
  findRynReplayGridSnapshotPage,
  rowsFromKendoGridData,
} from '@ups/xplat/web/core';

/**
 * One-line blocked-grid snapshot wiring (#31398).
 *
 * ```html
 * <kendo-grid rynReplayGridSnapshot="equipment-usage" …>
 * ```
 *
 * Page id must exist in `provideRynReplayGridSnapshots(...)`. Tracks when the
 * grid's bound data reference changes; emits leave on destroy. No page.ts hooks.
 */
@Directive({
  selector: 'kendo-grid[rynReplayGridSnapshot]',
  standalone: true,
})
export class RynReplayGridSnapshotDirective implements DoCheck, OnDestroy {
  @Input({ required: true, alias: 'rynReplayGridSnapshot' })
  pageId!: string;

  private readonly grid = inject(GridComponent);
  private readonly snapshots = inject(RynReplayGridSnapshotService);
  private readonly config = inject(RYN_REPLAY_GRID_SNAPSHOTS_CONFIG, {
    optional: true,
  });

  private lastDataRef: unknown = undefined;
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;
  private left = false;

  ngDoCheck(): void {
    if (!this.pageId || this.left) {
      return;
    }
    const data = this.grid?.data;
    const ref =
      data && typeof data === 'object' && 'data' in (data as object)
        ? (data as { data: unknown }).data
        : data;
    if (ref === this.lastDataRef) {
      return;
    }
    this.lastDataRef = ref;
    this.scheduleTrack();
  }

  ngOnDestroy(): void {
    this.clearDebounce();
    this.left = true;
    try {
      if (this.pageId) {
        this.snapshots.leave(this.pageId);
      }
    } catch {
      // best-effort
    }
  }

  private scheduleTrack(): void {
    this.clearDebounce();
    this.debounceTimer = setTimeout(() => this.trackNow(), 250);
  }

  private clearDebounce(): void {
    if (this.debounceTimer !== undefined) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = undefined;
    }
  }

  private trackNow(): void {
    if (!this.pageId || this.left) {
      return;
    }
    const page = findRynReplayGridSnapshotPage(this.config, this.pageId);
    const requireField = page?.requireField;
    const raw = rowsFromKendoGridData(this.grid?.data);
    const skip =
      typeof this.grid?.skip === 'number' ? this.grid.skip : undefined;
    const take =
      typeof this.grid?.pageSize === 'number' ? this.grid.pageSize : undefined;

    this.snapshots.track(this.pageId, {
      reason: 'data',
      rows: raw,
      grid: this.grid,
      skip,
      take,
      includeRow: requireField
        ? (row) =>
            !!row &&
            typeof row === 'object' &&
            !!(row as Record<string, unknown>)[requireField]
        : undefined,
    });
  }
}
